import React, {useState, useEffect, useMemo, useCallback} from 'react';
import {useMaterialReactTable, MaterialReactTable} from 'material-react-table';
import {Box, Typography, Button, IconButton, Avatar, Tooltip, Link} from '@mui/material';
import {Link as RouterLink, useNavigate} from 'react-router-dom';
import apiService from '../../services/apiService';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from "@mui/icons-material/Delete";
import ConfirmDialog from "../../components/ConfirmDialog";
import InlineToggleCell from "../../components/InlineToggleCell";
import {ROLES} from "../../config/roles";
import Protected from "../../components/Protected";

const CategoriesPage = () => {
    const navigate = useNavigate();
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [itemToDelete, setItemToDelete] = useState(null);

    const fetchCategories = useCallback(() => {
        setLoading(true);
        apiService.get('/api/categories')
            .then(data => setCategories(data))
            .catch(err => setError(err.message || 'Failed to fetch categories'))
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => {
        fetchCategories();
    }, [fetchCategories]);

    const handleDeleteItem = async () => {
        try {
            await apiService.delete(`/api/categories/${itemToDelete.id}`);
            fetchCategories(); // Refresh list
        } catch (err) {
            setError('Failed to delete category.');
        } finally {
            setConfirmOpen(false);
            setItemToDelete(null);
        }
    };

    const columns = useMemo(() => [
        {
            accessorKey: 'id',
            header: 'ID',
            size: 50,
            Cell: ({cell}) => (
                <Link component={RouterLink} to={`/categories/${cell.getValue()}`}>
                    {cell.getValue()}
                </Link>
            ),
        },
        {
            accessorKey: 'imageUrl',
            header: 'Image',
            size: 60,
            Cell: ({cell, row}) => (
                <Avatar src={cell.getValue()} alt={row.original.name}/>
            ),
        },
        {
            accessorKey: 'name',
            header: 'Name',
            Cell: ({cell, row}) => (
                <Link component={RouterLink} to={`/categories/${row.original.id}`}>
                    {cell.getValue()}
                </Link>
            ),
        },
        {
            accessorKey: 'identifier',
            header: 'Identifier',
        },
        {
            accessorKey: 'price',
            header: 'Price',
            size: 100,
        },
        {
            accessorKey: 'active',
            header: 'Status',
            size: 100,
            // InlineToggleCell handles auth, optimistic UI, error rollback,
            // and stopPropagation so the row's Edit-link click doesn't fire.
            // PUT /api/categories/{id}/active returns the full refreshed row;
            // we splice it into local state via `onSaved` — no full refetch.
            Cell: ({row}) => (
                <InlineToggleCell
                    protectedBy={CategoriesPage}
                    protectAction="update"
                    value={row.original.active}
                    save={(next) => apiService.put(
                        `/api/categories/${row.original.id}/active`,
                        {value: next},
                    )}
                    onSaved={(updated) => setCategories(prev =>
                        prev.map(c => c.id === updated.id ? updated : c)
                    )}
                    tooltip="Click to toggle active"
                />
            ),
        },
    ], []);

    const table = useMaterialReactTable({
        columns,
        data: categories,
        state: {isLoading: loading, showAlertBanner: !!error},
        muiToolbarAlertBannerProps: error ? {color: 'error', children: `Error: ${error}`} : undefined,
        renderTopToolbarCustomActions: () => (
            <Protected component={CategoriesPage} action="create">
                <Button
                    variant="contained"
                    startIcon={<AddIcon/>}
                    onClick={() => navigate('/categories/new')}
                >
                    Create Category
                </Button>
            </Protected>

        ),
        enableRowActions: true,
        displayColumnDefOptions: {'mrt-row-actions': {header: 'Actions', size: 100}},
        positionActionsColumn: 'last',
        renderRowActions: ({row}) => (
            <Box sx={{display: 'flex', gap: '0.5rem'}}>
                <Tooltip title="Edit">
                    {/* The link now goes to the unified edit page, NOT a detail page */}
                    <IconButton component={RouterLink} to={`/categories/${row.original.id}`}>
                        <EditIcon/>
                    </IconButton>
                </Tooltip>
                <Protected component={CategoriesPage} action="delete">
                    <Tooltip title="Delete">
                        <IconButton
                            color="error"
                            onClick={() => {
                                setItemToDelete(row.original);
                                setConfirmOpen(true);
                            }}
                        >
                            <DeleteIcon/>
                        </IconButton>
                    </Tooltip>
                </Protected>
            </Box>
        ),
    });

    return (
        <Box sx={{p: 2}}>
            <Typography variant="h4" gutterBottom>
                Categories
            </Typography>
            <MaterialReactTable table={table}/>
            <Protected component={CategoriesPage} action="delete">
                <ConfirmDialog
                    open={confirmOpen}
                    onClose={() => setConfirmOpen(false)}
                    onConfirm={handleDeleteItem}
                    title={`Delete Category: ${itemToDelete?.name}?`}
                    message="Are you sure you want to delete this category? This action cannot be undone."
                />
            </Protected>
        </Box>
    );
};

CategoriesPage.abilities = {
    read: [ROLES.VIEWERS, ROLES.SUPPORT, ROLES.ADMINS],
    create: [ROLES.ADMINS],
    update: [ROLES.SUPPORT, ROLES.ADMINS],
    delete: [ROLES.ADMINS],
};

export default CategoriesPage;